import * as React from 'react';

import type { BlockUpdatesData, BlockUpdatesDateFormat } from '@gitbook/doc-core';
import { updates } from '@gitbook/doc-core';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { PlaceholderButton } from '@gitbook/spine-react';

import { BlockWrapper, useBlockPaletteFactory } from '../common';

export interface UpdatesFormatContextValue {
    format: BlockUpdatesDateFormat;
    updateFormat: (format: BlockUpdatesDateFormat) => void;
}

export const UpdatesFormatContext = React.createContext<UpdatesFormatContextValue | null>(null);

export function BlockUpdates(props: BlockProps) {
    const { node, editor } = props;
    const palette = useBlockPaletteFactory({ nodeKey: node.key });

    // Read format from the Updates block's data
    const updatesData = node.data.toFrozenObject<BlockUpdatesData>();
    const format = updatesData.format;

    const updateFormat = React.useCallback(
        (nextFormat: BlockUpdatesDateFormat) => {
            if (nextFormat === format) {
                return;
            }

            editor.change((change) => {
                change.setNodeByKey(node.key, {
                    data: node.data.set('format', nextFormat),
                });
            });
        },
        [editor, node.key, node.data, format]
    );

    const formatContextValue = React.useMemo(
        () => ({ format, updateFormat }),
        [format, updateFormat]
    );

    const handleAddUpdate = ({ position }: { position: 'before' | 'after' }) => {
        editor.change((change) => {
            updates.insertUpdateAt(change, node.key, position === 'before' ? 0 : node.nodes.size);
        });
    };

    return (
        <UpdatesFormatContext.Provider value={formatContextValue}>
            <BlockWrapper {...props} palette={palette} contentProps={{ className: 'relative' }}>
                {!editor.readOnly ? (
                    <PlaceholderButton
                        size="small"
                        icon={Icon.Plus}
                        className="w-full"
                        label="Insert update"
                        onPress={() => handleAddUpdate({ position: 'before' })}
                    />
                ) : null}

                {props.children}

                {!editor.readOnly ? (
                    <PlaceholderButton
                        size="small"
                        icon={Icon.Plus}
                        className="w-full"
                        label="Insert update"
                        onPress={() => handleAddUpdate({ position: 'after' })}
                    />
                ) : null}
            </BlockWrapper>
        </UpdatesFormatContext.Provider>
    );
}
